import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isMeetingInProgress } from "@/lib/agent/genieMeetings";
import { genieMeetingService } from "@/services/agent/GenieMeetingService";
import {
  CalendarProvider,
  GenieCalendarConnection,
  GenieCalendars,
  MeetingNotesParts,
  SendGenieToMeetingPayload,
  UpdateGenieCalendarPayload,
} from "@/types/agent/genie-meeting";

// Safety net for a missed broadcast while the genie is in, or wrapping up, a meeting.
const IN_PROGRESS_REFETCH_MS = 20_000;

// Mirrors the `genie-meetings-${agent_id}` broadcast sent by the api handlers
// and by supabase/functions/webhooks/nylas.ts whenever a meeting changes.
export const useGenieMeetingsRealtime = (agentId: string | undefined) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!agentId) return;

    const channel = supabase
      .channel(`genie-meetings-${agentId}`)
      .on("broadcast", { event: "meeting_updated" }, ({ payload }: { payload: { meeting_id?: string } }) => {
        queryClient.invalidateQueries({ queryKey: ["genie-meetings", agentId] });
        if (payload.meeting_id) {
          queryClient.invalidateQueries({ queryKey: ["genie-meeting", payload.meeting_id] });
        }
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [agentId, queryClient]);
};

export const useGenieMeetings = (agentId: string | undefined) =>
  useQuery({
    queryKey: ["genie-meetings", agentId],
    queryFn: () => genieMeetingService.list(agentId!),
    enabled: !!agentId,
    refetchInterval: (query) => (query.state.data?.some(isMeetingInProgress) ? IN_PROGRESS_REFETCH_MS : false),
  });

export const useGenieMeetingUsage = (agentId: string | undefined) =>
  useQuery({
    queryKey: ["genie-meetings", agentId, "usage"],
    queryFn: () => genieMeetingService.usage(agentId!),
    enabled: !!agentId,
  });

export const useGenieMeeting = (meetingId: string | undefined) =>
  useQuery({
    queryKey: ["genie-meeting", meetingId],
    queryFn: () => genieMeetingService.get(meetingId!),
    enabled: !!meetingId,
    // The open drawer's Activity list should keep moving even if a broadcast is missed.
    refetchInterval: (query) => (query.state.data && isMeetingInProgress(query.state.data) ? IN_PROGRESS_REFETCH_MS : false),
  });

export const useSendGenieToMeeting = (agentId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SendGenieToMeetingPayload) => genieMeetingService.send(agentId!, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["genie-meetings", agentId] }),
  });
};

export const useCancelGenieMeeting = (agentId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (meetingId: string) => genieMeetingService.cancel(meetingId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["genie-meetings", agentId] }),
  });
};

export const useLeaveGenieMeeting = () =>
  useMutation({
    mutationFn: (meetingId: string) => genieMeetingService.leave(meetingId),
  });

export const useForceStopGenieMeeting = (agentId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (meetingId: string) => genieMeetingService.forceStop(meetingId),
    onSuccess: (_, meetingId) => {
      queryClient.invalidateQueries({ queryKey: ["genie-meetings", agentId] });
      queryClient.invalidateQueries({ queryKey: ["genie-meeting", meetingId] });
    },
  });
};

/** Admin only. Fetched on demand: it is a live call to Nylas. */
export const useAdminNotetakerHistory = (meetingId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ["genie-meeting", meetingId, "nylas-history"],
    queryFn: () => genieMeetingService.adminHistory(meetingId!),
    enabled: !!meetingId && enabled,
    staleTime: 0,
  });

export const useAddMeetingToKnowledge =(agentId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ meetingId, parts }: { meetingId: string; parts: MeetingNotesParts }) =>
      genieMeetingService.addToKnowledge(meetingId, parts),
    onSuccess: (meeting) => {
      queryClient.invalidateQueries({ queryKey: ["genie-meetings", agentId] });
      queryClient.invalidateQueries({ queryKey: ["genie-meeting", meeting.id] });
      queryClient.invalidateQueries({ queryKey: ["agent", agentId] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
    },
  });
};

export const useDeleteGenieMeeting = (agentId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (meetingId: string) => genieMeetingService.delete(meetingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["genie-meetings", agentId] });
      // Its Inbox conversation is deleted with it.
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
};

// Under the genie's meetings key, so every meeting broadcast refreshes them too.
const calendarKey = (agentId: string | undefined) => ["genie-meetings", agentId, "calendar"];
// Every calendar's upcoming list: a booking from one calendar changes what the others show.
const calendarEventsKey = (agentId: string | undefined) => ["genie-meetings", agentId, "calendar-events"];

/** Puts a saved calendar into the genie's cached list of calendars. */
const withConnection = (calendars: GenieCalendars | undefined, connection: GenieCalendarConnection) =>
  calendars && {
    ...calendars,
    connections: calendars.connections.map((current) => (current.id === connection.id ? connection : current)),
  };

export const useGenieCalendars = (agentId: string | undefined) =>
  useQuery({
    queryKey: calendarKey(agentId),
    queryFn: () => genieMeetingService.getCalendars(agentId!),
    enabled: !!agentId,
  });

export const useGenieCalendarList = (agentId: string | undefined, connectionId: string, enabled: boolean) =>
  useQuery({
    queryKey: ["genie-meetings", agentId, "calendar-list", connectionId],
    queryFn: () => genieMeetingService.listCalendars(agentId!, connectionId),
    enabled: !!agentId && enabled,
  });

export const useSelectGenieCalendar = (agentId: string | undefined, connectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (calendarId: string) => genieMeetingService.selectCalendar(agentId!, connectionId, calendarId),
    onSuccess: (connection) => {
      queryClient.setQueryData<GenieCalendars | undefined>(calendarKey(agentId), (calendars) =>
        withConnection(calendars, connection),
      );
      queryClient.invalidateQueries({ queryKey: calendarEventsKey(agentId) });
      queryClient.invalidateQueries({ queryKey: ["genie-meetings", agentId], exact: true });
    },
  });
};

export const useGenieCalendarEvents = (agentId: string | undefined, connectionId: string, enabled: boolean) =>
  useQuery({
    queryKey: [...calendarEventsKey(agentId), connectionId],
    queryFn: () => genieMeetingService.calendarEvents(agentId!, connectionId),
    enabled: !!agentId && enabled,
  });

/**
 * Sends the browser to the provider's sign-in; its calendar callback page finishes the connection.
 * With connectionId it connects that calendar again, otherwise it adds one.
 */
export const useConnectGenieCalendar = (agentId: string | undefined) =>
  useMutation({
    mutationFn: ({ provider, connectionId }: { provider: CalendarProvider; connectionId?: string }) =>
      genieMeetingService.connectCalendar(agentId!, provider, connectionId),
    onSuccess: (url) => {
      window.location.href = url;
    },
  });

export const useCompleteGenieCalendarConnect = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ code, state }: { code: string; state: string }) =>
      genieMeetingService.completeCalendarConnect(code, state),
    onSuccess: ({ agentId }) => queryClient.invalidateQueries({ queryKey: ["genie-meetings", agentId] }),
  });
};

export const useUpdateGenieCalendar = (agentId: string | undefined, connectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateGenieCalendarPayload) =>
      genieMeetingService.updateCalendar(agentId!, connectionId, payload),
    onSuccess: (connection) => {
      queryClient.setQueryData<GenieCalendars | undefined>(calendarKey(agentId), (calendars) =>
        withConnection(calendars, connection),
      );
      queryClient.invalidateQueries({ queryKey: calendarEventsKey(agentId) });
    },
  });
};

export const useDisconnectGenieCalendar = (agentId: string | undefined, connectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => genieMeetingService.disconnectCalendar(agentId!, connectionId),
    onSuccess: () => {
      queryClient.setQueryData<GenieCalendars | undefined>(
        calendarKey(agentId),
        (calendars) =>
          calendars && {
            ...calendars,
            connections: calendars.connections.filter((connection) => connection.id !== connectionId),
          },
      );
      queryClient.removeQueries({ queryKey: [...calendarEventsKey(agentId), connectionId] });
      queryClient.invalidateQueries({ queryKey: ["genie-meetings", agentId] });
    },
  });
};
